import React, { useState, useEffect } from 'react';
import flightService, { Flight, CreateFlightRequest, UpdateFlightRequest, PaginatedResponse, FlightStats } from '../services/flightService';
import './FlightManagement.css';

interface FlightManagementProps {
  onClose?: () => void;
}

const FlightManagement: React.FC<FlightManagementProps> = ({ onClose }) => {
  const [flights, setFlights] = useState<Flight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingFlight, setEditingFlight] = useState<Flight | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [selectedDepartureAirport, setSelectedDepartureAirport] = useState<string>('');
  const [selectedArrivalAirport, setSelectedArrivalAirport] = useState<string>('');
  const [selectedCarrier, setSelectedCarrier] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [flightStats, setFlightStats] = useState<FlightStats | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('ASC');
  const [selectedFlights, setSelectedFlights] = useState<number[]>([]);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  // Формы
  const [createForm, setCreateForm] = useState<CreateFlightRequest>({
    flightNumber: '',
    departureAirport: '',
    arrivalAirport: '',
    departureTime: '',
    arrivalTime: '',
    aircraftTypeId: 1,
    carrierId: 1,
    statusId: 1
  });

  const [editForm, setEditForm] = useState<UpdateFlightRequest>({
    flightNumber: '',
    departureAirport: '',
    arrivalAirport: '',
    departureTime: '',
    arrivalTime: '',
    aircraftTypeId: 1,
    carrierId: 1,
    statusId: 1
  });

  const airports = ['ASTANA', 'ALMATY', 'ALA', 'NQZ', 'GUW', 'URA', 'AKX', 'SCO', 'KGF'];
  const carriers = [
    { id: 1, name: 'Air Astana', code: 'KC' },
    { id: 2, name: 'Scat Airlines', code: 'DV' },
    { id: 3, name: 'Qazaq Air', code: 'IQ' }
  ];
  const aircraftTypes = [
    { id: 1, code: 'B737', name: 'Boeing 737' },
    { id: 2, code: 'A320', name: 'Airbus A320' },
    { id: 3, code: 'B777', name: 'Boeing 777' }
  ];
  const flightStatuses = [
    { id: 1, name: 'Запланирован', color: '#3B82F6' },
    { id: 2, name: 'Выполняется', color: '#F59E0B' },
    { id: 3, name: 'Завершен', color: '#10B981' },
    { id: 4, name: 'Отменен', color: '#EF4444' },
    { id: 5, name: 'Задержан', color: '#8B5CF6' }
  ];

  const sortOptions = [
    { value: 'id', label: 'ID' },
    { value: 'flightNumber', label: 'Номер рейса' },
    { value: 'departureTime', label: 'Время вылета' },
    { value: 'arrivalTime', label: 'Время прилета' },
    { value: 'departureAirport', label: 'Аэропорт вылета' },
    { value: 'arrivalAirport', label: 'Аэропорт прилета' },
    { value: 'createdAt', label: 'Дата создания' }
  ];

  useEffect(() => {
    fetchFlights();
    fetchFlightStats();
  }, [currentPage, pageSize, sortBy, sortDir]);

  const fetchFlights = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: PaginatedResponse<Flight> = await flightService.getFlights(currentPage, pageSize, sortBy, sortDir);
      setFlights(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err) {
      console.error('Error fetching flights:', err);
      setError('Ошибка при загрузке рейсов');
    } finally {
      setLoading(false);
    }
  };

  const fetchFlightStats = async () => {
    try {
      const stats = await flightService.getFlightStats();
      setFlightStats(stats);
    } catch (err) {
      console.error('Error fetching flight stats:', err);
    }
  };

  const handleCreateFlight = async () => {
    try {
      // Проверяем, не существует ли уже рейс с таким номером
      const existingFlight = flights.find(flight => 
        flight.flightNumber.toLowerCase() === createForm.flightNumber.toLowerCase()
      );
      
      if (existingFlight) {
        alert(`Рейс с номером "${createForm.flightNumber}" уже существует!`);
        return;
      }

      // Валидация длины номера рейса
      if (createForm.flightNumber.length < 3 || createForm.flightNumber.length > 10) {
        alert('Номер рейса должен содержать от 3 до 10 символов');
        return;
      }

      await flightService.createFlight(createForm);
      setIsCreateModalOpen(false);
      setCreateForm({
        flightNumber: '',
        departureAirport: '',
        arrivalAirport: '',
        departureTime: '',
        arrivalTime: '',
        aircraftTypeId: 1,
        carrierId: 1,
        statusId: 1
      });
      setValidationErrors({});
      fetchFlights();
      fetchFlightStats();
      alert('Рейс успешно создан!');
    } catch (err) {
      console.error('Error creating flight:', err);
      alert('Ошибка при создании рейса');
    }
  };

  const handleUpdateFlight = async () => {
    try {
      if (!editingFlight) return;

      // Проверяем, не существует ли уже рейс с таким номером (кроме текущего)
      if (editForm.flightNumber && editForm.flightNumber.trim()) {
        const existingFlight = flights.find(flight => 
          flight.flightNumber.toLowerCase() === editForm.flightNumber!.toLowerCase() &&
          flight.id !== editingFlight.id
        );
        
        if (existingFlight) {
          alert(`Рейс с номером "${editForm.flightNumber}" уже существует!`);
          return;
        }
      }

      // Валидация длины номера рейса при обновлении
      if (editForm.flightNumber && (editForm.flightNumber.length < 3 || editForm.flightNumber.length > 10)) {
        alert('Номер рейса должен содержать от 3 до 10 символов');
        return;
      }

      const updateData: UpdateFlightRequest = {};
      if (editForm.flightNumber) updateData.flightNumber = editForm.flightNumber;
      if (editForm.departureAirport) updateData.departureAirport = editForm.departureAirport;
      if (editForm.arrivalAirport) updateData.arrivalAirport = editForm.arrivalAirport;
      if (editForm.departureTime) updateData.departureTime = editForm.departureTime;
      if (editForm.arrivalTime) updateData.arrivalTime = editForm.arrivalTime;
      if (editForm.aircraftTypeId) updateData.aircraftTypeId = editForm.aircraftTypeId;
      if (editForm.carrierId) updateData.carrierId = editForm.carrierId;
      if (editForm.statusId) updateData.statusId = editForm.statusId;

      await flightService.updateFlight(editingFlight.id, updateData);
      setEditingFlight(null);
      setEditForm({
        flightNumber: '',
        departureAirport: '',
        arrivalAirport: '',
        departureTime: '',
        arrivalTime: '',
        aircraftTypeId: 1,
        carrierId: 1,
        statusId: 1
      });
      setValidationErrors({});
      fetchFlights();
      fetchFlightStats();
      alert('Рейс успешно обновлен!');
    } catch (err) {
      console.error('Error updating flight:', err);
      alert('Ошибка при обновлении рейса');
    }
  };

  const handleDeleteFlight = async (flight: Flight) => {
    if (!window.confirm(`Вы уверены, что хотите удалить рейс "${flight.flightNumber}"?`)) {
      return;
    }

    try {
      await flightService.deleteFlight(flight.id);
      fetchFlights();
      fetchFlightStats();
      alert('Рейс успешно удален!');
    } catch (err) {
      console.error('Error deleting flight:', err);
      alert('Ошибка при удалении рейса');
    }
  };

  const handleCreateDefaultFlights = async () => {
    try {
      const defaultFlights = flightService.getDefaultFlights();
      
      // Проверяем существующие номера рейсов
      const existingFlightNumbers = flights.map(flight => flight.flightNumber);
      const flightsToCreate = defaultFlights.filter(flight => 
        !existingFlightNumbers.includes(flight.flightNumber)
      );

      if (flightsToCreate.length === 0) {
        alert('Все стандартные рейсы уже существуют!');
        return;
      }

      let createdCount = 0;
      for (const flightData of flightsToCreate) {
        try {
          await flightService.createFlight(flightData);
          createdCount++;
        } catch (err) {
          console.error(`Error creating flight ${flightData.flightNumber}:`, err);
        }
      }

      fetchFlights();
      fetchFlightStats();
      
      if (createdCount > 0) {
        alert(`Создано ${createdCount} новых рейсов из ${defaultFlights.length} стандартных!`);
      } else {
        alert('Не удалось создать ни одного рейса');
      }
    } catch (err) {
      console.error('Error creating default flights:', err);
      alert('Ошибка при создании стандартных рейсов');
    }
  };

  const handleBulkAction = async (action: 'activate' | 'deactivate' | 'delete') => {
    if (selectedFlights.length === 0) {
      alert('Выберите рейсы для выполнения действия');
      return;
    }

    const confirmMessage = {
      activate: `Активировать ${selectedFlights.length} рейсов?`,
      deactivate: `Деактивировать ${selectedFlights.length} рейсов?`,
      delete: `Удалить ${selectedFlights.length} рейсов?`
    };

    if (!window.confirm(confirmMessage[action])) {
      return;
    }

    try {
      for (const flightId of selectedFlights) {
        switch (action) {
          case 'activate':
            // Логика активации рейса
            break;
          case 'deactivate':
            // Логика деактивации рейса
            break;
          case 'delete':
            await flightService.deleteFlight(flightId);
            break;
        }
      }
      setSelectedFlights([]);
      fetchFlights();
      fetchFlightStats();
      alert(`Действие выполнено для ${selectedFlights.length} рейсов`);
    } catch (err) {
      console.error('Error performing bulk action:', err);
      alert('Ошибка при выполнении действия');
    }
  };

  const handleSelectFlight = (flightId: number) => {
    setSelectedFlights(prev => 
      prev.includes(flightId) 
        ? prev.filter(id => id !== flightId)
        : [...prev, flightId]
    );
  };

  const handleSelectAll = () => {
    if (selectedFlights.length === filteredFlights.length) {
      setSelectedFlights([]);
    } else {
      setSelectedFlights(filteredFlights.map(flight => flight.id));
    }
  };

  const validateFlightNumber = (flightNumber: string): string | null => {
    if (!flightNumber) return null;
    if (flightNumber.length < 3) return 'Номер рейса должен содержать минимум 3 символа';
    if (flightNumber.length > 10) return 'Номер рейса должен содержать максимум 10 символов';
    return null;
  };

  const handleCreateFormChange = (field: keyof CreateFlightRequest, value: string | number) => {
    setCreateForm({ ...createForm, [field]: value });
    
    // Валидация в реальном времени
    if (field === 'flightNumber') {
      const error = validateFlightNumber(value as string);
      setValidationErrors(prev => ({
        ...prev,
        createFlightNumber: error || ''
      }));
    }
  };

  const handleEditFormChange = (field: keyof UpdateFlightRequest, value: string | number) => {
    setEditForm({ ...editForm, [field]: value });
    
    // Валидация в реальном времени
    if (field === 'flightNumber') {
      const error = validateFlightNumber(value as string);
      setValidationErrors(prev => ({
        ...prev,
        editFlightNumber: error || ''
      }));
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortDir('ASC');
    }
  };

  const getAirportText = (airport: string) => {
    const airportMap: Record<string, string> = {
      'ALA': 'Алматы',
      'NQZ': 'Нур-Султан',
      'GUW': 'Атырау',
      'URA': 'Уральск',
      'AKX': 'Актобе',
      'SCO': 'Актау',
      'KGF': 'Караганда',
      'ASTANA': 'Астана',
      'ALMATY': 'Алматы'
    };
    return airportMap[airport] || airport;
  };

  const getStatusText = (statusId: number) => {
    const status = flightStatuses.find(s => s.id === statusId);
    return status ? status.name : 'Неизвестно';
  };

  const getStatusColor = (statusId: number) => {
    const status = flightStatuses.find(s => s.id === statusId);
    return status ? status.color : '#6B7280';
  };

  const filteredFlights = flights.filter(flight => {
    const matchesSearch = 
      flight.flightNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getAirportText(flight.departureAirport).toLowerCase().includes(searchTerm.toLowerCase()) ||
      getAirportText(flight.arrivalAirport).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActive = !showActiveOnly || flight.isActive;
    const matchesDeparture = !selectedDepartureAirport || flight.departureAirport === selectedDepartureAirport;
    const matchesArrival = !selectedArrivalAirport || flight.arrivalAirport === selectedArrivalAirport;
    const matchesCarrier = !selectedCarrier || flight.carrier.id.toString() === selectedCarrier;
    return matchesSearch && matchesActive && matchesDeparture && matchesArrival && matchesCarrier;
  });

  const openEditModal = (flight: Flight) => {
    setEditingFlight(flight);
    setEditForm({
      flightNumber: flight.flightNumber,
      departureAirport: flight.departureAirport,
      arrivalAirport: flight.arrivalAirport,
      departureTime: flight.departureTime,
      arrivalTime: flight.arrivalTime,
      aircraftTypeId: flight.aircraftType.id,
      carrierId: flight.carrier.id,
      statusId: flight.status.id
    });
  };

  const closeEditModal = () => {
    setEditingFlight(null);
    setEditForm({
      flightNumber: '',
      departureAirport: '',
      arrivalAirport: '',
      departureTime: '',
      arrivalTime: '',
      aircraftTypeId: 1,
      carrierId: 1,
      statusId: 1
    });
    setValidationErrors({});
  };

  const handleRemoveDuplicates = async () => {
    try {
      // Находим дублированные рейсы по номеру
      const flightNumbers = flights.map(flight => flight.flightNumber);
      const duplicates = flightNumbers.filter((number, index) => 
        flightNumbers.indexOf(number) !== index
      );
      
      if (duplicates.length === 0) {
        alert('Дублированных рейсов не найдено!');
        return;
      }

      const uniqueDuplicates = Array.from(new Set(duplicates));
      const flightsToDelete: Flight[] = [];

      // Для каждого дублированного номера оставляем только первый рейс
      uniqueDuplicates.forEach(flightNumber => {
        const flightsWithNumber = flights.filter(flight => 
          flight.flightNumber === flightNumber
        );
        // Удаляем все кроме первого
        flightsToDelete.push(...flightsWithNumber.slice(1));
      });

      if (!window.confirm(`Найдено ${flightsToDelete.length} дублированных рейсов. Удалить их?`)) {
        return;
      }

      let deletedCount = 0;
      for (const flight of flightsToDelete) {
        try {
          await flightService.deleteFlight(flight.id);
          deletedCount++;
        } catch (err) {
          console.error(`Error deleting duplicate flight ${flight.flightNumber}:`, err);
        }
      }

      fetchFlights();
      fetchFlightStats();
      alert(`Удалено ${deletedCount} дублированных рейсов!`);
    } catch (err) {
      console.error('Error removing duplicates:', err);
      alert('Ошибка при удалении дублированных рейсов');
    }
  };

  if (loading) {
    return (
      <div className="flight-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка рейсов...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flight-management">
      <div className="flight-header">
        <div className="header-left">
          <h2>Управление рейсами</h2>
          {flightStats && (
            <div className="quick-stats">
              <span className="stat-item">
                <strong>{totalElements}</strong> всего
              </span>
              <span className="stat-item">
                <strong>{flightStats.activeFlights}</strong> активных
              </span>
              <span className="stat-item">
                <strong>{flightStats.completedFlights}</strong> завершенных
              </span>
              {(() => {
                const flightNumbers = flights.map(flight => flight.flightNumber);
                const duplicates = flightNumbers.filter((number, index) => 
                  flightNumbers.indexOf(number) !== index
                );
                const uniqueDuplicates = Array.from(new Set(duplicates));
                if (uniqueDuplicates.length > 0) {
                  return (
                    <span className="stat-item duplicate-warning">
                      <strong>{uniqueDuplicates.length}</strong> дубликатов
                    </span>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </div>
        <div className="flight-actions">
          <button 
            onClick={() => setIsCreateModalOpen(true)} 
            className="btn btn-primary"
          >
            Создать рейс
          </button>
          <div className="bulk-actions">
            <button onClick={() => handleBulkAction('activate')} className="bulk-activate-btn">
              Активировать выбранных
            </button>
              <button onClick={() => handleBulkAction('delete')} className="bulk-delete-btn">
              Удалить выбранных
            </button>
          </div>
          <button 
            onClick={handleCreateDefaultFlights} 
            className="btn btn-secondary"
          >
            Стандартные рейсы
          </button>
          <button 
            onClick={handleRemoveDuplicates} 
            className="btn btn-warning"
          >
            Удалить дубликаты
          </button>
        </div>
      </div>

      {error && (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchFlights} className="retry-button">
            Попробовать снова
          </button>
        </div>
      )}

      <div className="flight-filters">
        <div className="search-section">
          <input
            type="text"
            placeholder="Поиск по номеру рейса или аэропортам..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-section">
          <select
            value={selectedDepartureAirport}
            onChange={(e) => setSelectedDepartureAirport(e.target.value)}
            className="airport-filter"
          >
            <option value="">Все аэропорты вылета</option>
            {airports.map(airport => (
              <option key={airport} value={airport}>{getAirportText(airport)}</option>
            ))}
          </select>
          <select
            value={selectedArrivalAirport}
            onChange={(e) => setSelectedArrivalAirport(e.target.value)}
            className="airport-filter"
          >
            <option value="">Все аэропорты прилета</option>
            {airports.map(airport => (
              <option key={airport} value={airport}>{getAirportText(airport)}</option>
            ))}
          </select>
          <select
            value={selectedCarrier}
            onChange={(e) => setSelectedCarrier(e.target.value)}
            className="carrier-filter"
          >
            <option value="">Все перевозчики</option>
            {carriers.map(carrier => (
              <option key={carrier.id} value={carrier.id.toString()}>{carrier.name}</option>
            ))}
          </select>
          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
            />
            Только активные
          </label>
        </div>
        <div className="view-controls">
          <button onClick={() => setViewMode(viewMode === 'cards' ? 'table' : 'cards')} className="view-toggle-btn">
            {viewMode === 'cards' ? 'Список' : 'Карточки'}
          </button>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="page-size-select"
          >
            <option value={5}>5 на странице</option>
            <option value={10}>10 на странице</option>
            <option value={20}>20 на странице</option>
            <option value={50}>50 на странице</option>
          </select>
        </div>
      </div>

      <div className="flights-list">
        {viewMode === 'cards' ? (
          filteredFlights.map((flight) => (
            <div key={flight.id} className={`flight-card ${!flight.isActive ? 'inactive' : ''}`}>
              <div className="flight-info">
                <div className="flight-header-info">
                  <div className="flight-number">{flight.flightNumber}</div>
                  <h3 className="flight-route">
                    {getAirportText(flight.departureAirport)} → {getAirportText(flight.arrivalAirport)}
                  </h3>
                  <span 
                    className="flight-status-badge"
                    style={{ backgroundColor: getStatusColor(flight.status.id) }}
                  >
                    {getStatusText(flight.status.id)}
                  </span>
                </div>
                <div className="flight-details">
                  <p><strong>Время вылета:</strong> {new Date(flight.departureTime).toLocaleString('ru-RU')}</p>
                  <p><strong>Время прилета:</strong> {new Date(flight.arrivalTime).toLocaleString('ru-RU')}</p>
                  <p><strong>Перевозчик:</strong> {flight.carrier.name} ({flight.carrier.code})</p>
                  <p><strong>Тип самолета:</strong> {flight.aircraftType.name}</p>
                </div>
                <div className="flight-meta">
                  <span>Создан: {new Date(flight.createdAt).toLocaleDateString('ru-RU')}</span>
                  {flight.updatedAt && <span>Обновлен: {new Date(flight.updatedAt).toLocaleDateString('ru-RU')}</span>}
                </div>
              </div>
              <div className="flight-actions">
                <button 
                  onClick={() => openEditModal(flight)} 
                  className="btn btn-primary"
                >
                  Редактировать
                </button>
                <button 
                  onClick={() => handleDeleteFlight(flight)} 
                  className="btn btn-danger"
                >
                  Удалить
                </button>
              </div>
            </div>
          ))
        ) : (
          <table className="flight-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedFlights.length === filteredFlights.length && filteredFlights.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th onClick={() => handleSort('id')} className="sortable">
                  ID {sortBy === 'id' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('flightNumber')} className="sortable">
                  Номер рейса {sortBy === 'flightNumber' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('departureAirport')} className="sortable">
                  Вылет {sortBy === 'departureAirport' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('arrivalAirport')} className="sortable">
                  Прилет {sortBy === 'arrivalAirport' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('departureTime')} className="sortable">
                  Время вылета {sortBy === 'departureTime' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('arrivalTime')} className="sortable">
                  Время прилета {sortBy === 'arrivalTime' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th>Перевозчик</th>
                <th>Тип самолета</th>
                <th>Статус</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredFlights.map((flight) => (
                <tr key={flight.id} className={`flight-row ${!flight.isActive ? 'inactive' : ''}`}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedFlights.includes(flight.id)}
                      onChange={() => handleSelectFlight(flight.id)}
                    />
                  </td>
                  <td>{flight.id}</td>
                  <td>{flight.flightNumber}</td>
                  <td>{getAirportText(flight.departureAirport)}</td>
                  <td>{getAirportText(flight.arrivalAirport)}</td>
                  <td>{new Date(flight.departureTime).toLocaleString('ru-RU')}</td>
                  <td>{new Date(flight.arrivalTime).toLocaleString('ru-RU')}</td>
                  <td>{flight.carrier.name} ({flight.carrier.code})</td>
                  <td>{flight.aircraftType.name}</td>
                  <td>
                    <span 
                      className="flight-status-badge"
                      style={{ backgroundColor: getStatusColor(flight.status.id) }}
                    >
                      {getStatusText(flight.status.id)}
                    </span>
                  </td>
                  <td>
                    <button 
                      onClick={() => openEditModal(flight)} 
                      className="btn btn-primary"
                    >
                      Редактировать
                    </button>
                    <button 
                      onClick={() => handleDeleteFlight(flight)} 
                      className="btn btn-danger"
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {filteredFlights.length === 0 && !loading && (
        <div className="empty-state">
          <p>Рейсы не найдены</p>
        </div>
      )}

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <button onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0}>Назад</button>
          <span>Страница {currentPage + 1} из {totalPages}</span>
          <button onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1}>Вперед</button>
        </div>
      )}

      {/* Статистика */}
      {flightStats && (
        <div className="flight-stats">
          <h3>Статистика рейсов</h3>
          <p><strong>Всего рейсов:</strong> {totalElements}</p>
          <p><strong>Активных рейсов:</strong> {flightStats.activeFlights}</p>
          <p><strong>Завершенных рейсов:</strong> {flightStats.completedFlights}</p>
          <p><strong>Отмененных рейсов:</strong> {flightStats.cancelledFlights}</p>
          {flightStats.flightsByStatus && (
            <div className="status-stats">
              <h4>Рейсы по статусам:</h4>
              {Object.entries(flightStats.flightsByStatus).map(([status, count]) => (
                <p key={status}><strong>{status}:</strong> {String(count)}</p>
              ))}
            </div>
          )}
          {flightStats.flightsByCarrier && (
            <div className="carrier-stats">
              <h4>Рейсы по перевозчикам:</h4>
              {Object.entries(flightStats.flightsByCarrier).map(([carrier, count]) => (
                <p key={carrier}><strong>{carrier}:</strong> {String(count)}</p>
              ))}
            </div>
          )}
          {flightStats.flightsByAircraftType && (
            <div className="aircraft-stats">
              <h4>Рейсы по типам самолетов:</h4>
              {Object.entries(flightStats.flightsByAircraftType).map(([aircraft, count]) => (
                <p key={aircraft}><strong>{aircraft}:</strong> {String(count)}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Модальное окно создания */}
      {isCreateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Создать новый рейс</h3>
              <button onClick={() => setIsCreateModalOpen(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Номер рейса *</label>
                  <input
                    type="text"
                    value={createForm.flightNumber}
                    onChange={(e) => handleCreateFormChange('flightNumber', e.target.value)}
                    placeholder="KC-1234 (от 3 до 10 символов)"
                    className={validationErrors.createFlightNumber ? 'error' : ''}
                  />
                  {validationErrors.createFlightNumber && <p className="error-message">{validationErrors.createFlightNumber}</p>}
                </div>
                <div className="form-group">
                  <label>Перевозчик *</label>
                  <select
                    value={createForm.carrierId}
                    onChange={(e) => handleCreateFormChange('carrierId', Number(e.target.value))}
                  >
                    {carriers.map(carrier => (
                      <option key={carrier.id} value={carrier.id}>{carrier.name} ({carrier.code})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Аэропорт вылета *</label>
                  <select
                    value={createForm.departureAirport}
                    onChange={(e) => handleCreateFormChange('departureAirport', e.target.value)}
                  >
                    <option value="">Выберите аэропорт</option>
                    {airports.map(airport => (
                      <option key={airport} value={airport}>{getAirportText(airport)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Аэропорт прилета *</label>
                  <select
                    value={createForm.arrivalAirport}
                    onChange={(e) => handleCreateFormChange('arrivalAirport', e.target.value)}
                  >
                    <option value="">Выберите аэропорт</option>
                    {airports.map(airport => (
                      <option key={airport} value={airport}>{getAirportText(airport)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Время вылета *</label>
                  <input
                    type="datetime-local"
                    value={createForm.departureTime}
                    onChange={(e) => handleCreateFormChange('departureTime', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Время прилета *</label>
                  <input
                    type="datetime-local"
                    value={createForm.arrivalTime}
                    onChange={(e) => handleCreateFormChange('arrivalTime', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Тип самолета *</label>
                  <select
                    value={createForm.aircraftTypeId}
                    onChange={(e) => handleCreateFormChange('aircraftTypeId', Number(e.target.value))}
                  >
                    {aircraftTypes.map(aircraft => (
                      <option key={aircraft.id} value={aircraft.id}>{aircraft.name} ({aircraft.code})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Статус *</label>
                  <select
                    value={createForm.statusId}
                    onChange={(e) => handleCreateFormChange('statusId', Number(e.target.value))}
                  >
                    {flightStatuses.map(status => (
                      <option key={status.id} value={status.id}>{status.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => {
                setIsCreateModalOpen(false);
                setValidationErrors({});
              }} className="btn btn-secondary">
                Отмена
              </button>
              <button onClick={handleCreateFlight} className="btn btn-primary">
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно редактирования */}
      {editingFlight && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Редактировать рейс</h3>
              <button onClick={closeEditModal} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Номер рейса</label>
                  <input
                    type="text"
                    value={editForm.flightNumber}
                    onChange={(e) => handleEditFormChange('flightNumber', e.target.value)}
                    placeholder="KC-1234 (от 3 до 10 символов)"
                    className={validationErrors.editFlightNumber ? 'error' : ''}
                  />
                  {validationErrors.editFlightNumber && <p className="error-message">{validationErrors.editFlightNumber}</p>}
                </div>
                <div className="form-group">
                  <label>Перевозчик</label>
                  <select
                    value={editForm.carrierId}
                    onChange={(e) => handleEditFormChange('carrierId', Number(e.target.value))}
                  >
                    {carriers.map(carrier => (
                      <option key={carrier.id} value={carrier.id}>{carrier.name} ({carrier.code})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Аэропорт вылета</label>
                  <select
                    value={editForm.departureAirport}
                    onChange={(e) => handleEditFormChange('departureAirport', e.target.value)}
                  >
                    <option value="">Выберите аэропорт</option>
                    {airports.map(airport => (
                      <option key={airport} value={airport}>{getAirportText(airport)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Аэропорт прилета</label>
                  <select
                    value={editForm.arrivalAirport}
                    onChange={(e) => handleEditFormChange('arrivalAirport', e.target.value)}
                  >
                    <option value="">Выберите аэропорт</option>
                    {airports.map(airport => (
                      <option key={airport} value={airport}>{getAirportText(airport)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Время вылета</label>
                  <input
                    type="datetime-local"
                    value={editForm.departureTime}
                    onChange={(e) => handleEditFormChange('departureTime', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Время прилета</label>
                  <input
                    type="datetime-local"
                    value={editForm.arrivalTime}
                    onChange={(e) => handleEditFormChange('arrivalTime', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Тип самолета</label>
                  <select
                    value={editForm.aircraftTypeId}
                    onChange={(e) => handleEditFormChange('aircraftTypeId', Number(e.target.value))}
                  >
                    {aircraftTypes.map(aircraft => (
                      <option key={aircraft.id} value={aircraft.id}>{aircraft.name} ({aircraft.code})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Статус</label>
                  <select
                    value={editForm.statusId}
                    onChange={(e) => handleEditFormChange('statusId', Number(e.target.value))}
                  >
                    {flightStatuses.map(status => (
                      <option key={status.id} value={status.id}>{status.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={closeEditModal} className="btn btn-secondary">
                Отмена
              </button>
              <button onClick={handleUpdateFlight} className="btn btn-primary">
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightManagement; 